import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DEPARTURE_AT, DESTINATION, formatCoords, timeLeft, type TimeLeft } from '@/lib/journey'

const TICK_MS = 1000
const UNITS = ['days', 'hours', 'minutes', 'seconds'] as const satisfies readonly (keyof TimeLeft)[]

function useTimeLeft(target: number): TimeLeft | null {
  const [left, setLeft] = useState(() => timeLeft(target, Date.now()))
  useEffect(() => {
    const id = window.setInterval(() => setLeft(timeLeft(target, Date.now())), TICK_MS)
    return () => window.clearInterval(id)
  }, [target])
  return left
}

/** Una paletta dello split-flap: cambiando `char` la chiave rimonta lo span e la paletta ricade. */
export function Flap({ char }: { char: string }) {
  return (
    <span className="flap relative grid h-[1.3em] w-[0.9em] place-items-center overflow-hidden rounded-[0.12em] bg-ink-soft/35 [perspective:200px]">
      <span key={char} className="motion-safe:animate-flap">{char}</span>
    </span>
  )
}

/** Tabellone delle partenze: volo SM 0310 per Las Palmas, conto alla rovescia a paletta. */
export function DepartureBoard() {
  const { t } = useTranslation()
  const left = useTimeLeft(DEPARTURE_AT)
  return (
    <div className="rounded-xl bg-ink p-3 text-paper shadow-[0_18px_40px_-18px_var(--color-ink)] sm:p-4 short:p-3">
      <div className="flex items-center justify-between gap-3 font-body text-[0.65rem] font-semibold uppercase tracking-[0.18em] sm:text-xs">
        <span>
          {t('home.board.flight')} <span aria-hidden="true">→</span><span className="sr-only">{t('home.board.to')}</span> LPA
        </span>
        <span className="flex items-center gap-1.5 text-sun">
          <span aria-hidden="true" className="size-1.5 rounded-full bg-sun motion-safe:animate-pulse" />
          {left ? t('home.board.onTime') : t('home.board.departed')}
        </span>
      </div>

      {left ? (
        <div role="timer" aria-label={t('home.board.label')} data-testid="countdown" className="mt-3 grid grid-cols-4 gap-2 sm:gap-4">
          {UNITS.map((unit) => (
            <div key={unit} className="flex flex-col items-center">
              <span className="flex gap-[0.08em] font-body text-[clamp(1.75rem,min(10vw,6svh),3.75rem)] font-semibold leading-none tabular-nums">
                {String(left[unit]).padStart(2, '0').split('').map((char, i) => <Flap key={i} char={char} />)}
              </span>
              <span className="mt-1.5 font-body text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-paper/70 sm:text-[0.7rem]">{t(`home.board.units.${unit}`)}</span>
            </div>
          ))}
        </div>
      ) : (
        <p data-testid="countdown" className="mt-3 font-display text-3xl italic sm:text-5xl">{t('home.board.bonVoyage')}</p>
      )}

      <p className="mt-3 flex justify-between short:hidden gap-3 border-t border-paper/15 pt-2 font-body text-[0.65rem] tabular-nums tracking-[0.12em] text-paper/70 sm:text-xs">
        <span className="truncate">{DESTINATION.name}</span>
        <span className="whitespace-nowrap">{formatCoords(DESTINATION)}</span>
      </p>
    </div>
  )
}
